import Phaser from "phaser";
import { GAME_WIDTH } from "../constants";
import {
  RUN_CAMPEON_SONG_SYNC_CONFIG,
  type RunCampeonSongSyncEvent,
  type RunCampeonSyncPlacement
} from "../songSync/runCampeonSongSyncConfig";
import type { PickupData } from "../types";
import type { MapSystem } from "./MapSystem";

interface SongSyncCollectibleUpdate {
  songElapsedTime: number;
  worldSpeed: number;
  pickupLineY: number;
  map: MapSystem;
}

export class SongSyncCollectibleSystem {
  private spawned = new Set<string>();

  reset() {
    this.spawned.clear();
  }

  update({ songElapsedTime, worldSpeed, pickupLineY, map }: SongSyncCollectibleUpdate) {
    if (!RUN_CAMPEON_SONG_SYNC_CONFIG.enabled) return;

    const events = this.getEvents();
    for (const event of events) {
      if (this.spawned.has(event.id)) continue;

      const pickupSongTime = this.toSongTime(event.pickupCounterTime);
      const leadTime = event.spawnLeadTime ?? RUN_CAMPEON_SONG_SYNC_CONFIG.timing.defaultSpawnLeadTime;
      const spawnSongTime = pickupSongTime - leadTime;
      if (songElapsedTime < spawnSongTime) continue;

      if (songElapsedTime > pickupSongTime + RUN_CAMPEON_SONG_SYNC_CONFIG.timing.lateSkipSeconds) {
        this.spawned.add(event.id);
        continue;
      }

      this.spawnEvent(event, songElapsedTime, worldSpeed, pickupLineY, map);
      this.spawned.add(event.id);
    }
  }

  recordPickup(pickup: PickupData, songElapsedTime: number) {
    if (!RUN_CAMPEON_SONG_SYNC_CONFIG.debugSongSync.enabled || !pickup.songSyncEventId || pickup.targetCounterTime === undefined) {
      return;
    }

    const actualCounterTime = this.toCounterTime(songElapsedTime);
    const delta = actualCounterTime - pickup.targetCounterTime;
    console.log({
      eventId: pickup.songSyncEventId,
      targetPickupCounterTime: pickup.targetCounterTime,
      actualCounterTime,
      delta
    });
  }

  private spawnEvent(event: RunCampeonSongSyncEvent, songElapsedTime: number, worldSpeed: number, pickupLineY: number, map: MapSystem) {
    const collectible = RUN_CAMPEON_SONG_SYNC_CONFIG.collectibles[event.type];
    const pickupSongTime = this.toSongTime(event.pickupCounterTime);
    const count = Math.max(1, event.count);
    const middle = (count - 1) * 0.5;

    for (let index = 0; index < count; index += 1) {
      const itemOffset = count === 1 ? 0 : (index - middle) * RUN_CAMPEON_SONG_SYNC_CONFIG.timing.trailItemSpacingSeconds;
      const itemPickupSongTime = pickupSongTime + itemOffset;
      const travelTime = Phaser.Math.Clamp(itemPickupSongTime - songElapsedTime, 0.35, 3.2);
      const y = pickupLineY - worldSpeed * travelTime;
      const pathX = map.getPathXAt(y);
      const x = this.resolveX(pathX, event.placement, index, count);

      if (this.shouldReserveCorridor(event)) {
        const corridorRadius = collectible.magnetRadius + (event.intensity === "max" ? 128 : 96);
        map.clearSpawnArea(x, y, corridorRadius, { protectVisible: true });
        map.clearSpawnArea(x, (y + pickupLineY) * 0.5, corridorRadius, { protectVisible: true });
        map.clearSpawnArea(x, pickupLineY, corridorRadius, { protectVisible: true });
      }

      map.addPickupAt({
        id: `${event.id}-${index}`,
        x,
        y,
        radius: collectible.magnetRadius,
        collected: false,
        syncType: event.type,
        scoreValue: collectible.scoreValue,
        energyRestoreMultiplier: collectible.energyRestoreMultiplier,
        visualScale: collectible.visualScale,
        tint: collectible.tint,
        targetTimeSec: pickupSongTime,
        targetCounterTime: event.pickupCounterTime,
        songSyncEventId: event.id,
        intensity: event.intensity
      });
    }
  }

  private getEvents(): readonly RunCampeonSongSyncEvent[] {
    if (!RUN_CAMPEON_SONG_SYNC_CONFIG.predictedEventsEnabled) return RUN_CAMPEON_SONG_SYNC_CONFIG.events;
    return [...RUN_CAMPEON_SONG_SYNC_CONFIG.events, ...RUN_CAMPEON_SONG_SYNC_CONFIG.predictedEvents] as RunCampeonSongSyncEvent[];
  }

  private shouldReserveCorridor(event: RunCampeonSongSyncEvent) {
    return RUN_CAMPEON_SONG_SYNC_CONFIG.rules.reserveSafePathForMajorPickups && (event.intensity === "high" || event.intensity === "max");
  }

  private resolveX(pathX: number, placement: RunCampeonSyncPlacement, index: number, count: number) {
    if (placement === "safe_slight_offset") {
      const direction = index % 2 === 0 ? -1 : 1;
      return Phaser.Math.Clamp(pathX + direction * 72, 58, GAME_WIDTH - 58);
    }
    if (placement === "soft_curve_trail") {
      const t = count <= 1 ? 0 : index / (count - 1);
      return Phaser.Math.Clamp(pathX + Math.sin(t * Math.PI) * 68 - 34, 58, GAME_WIDTH - 58);
    }
    if (placement === "soft_center_trail" || placement === "center_trail") {
      const t = count <= 1 ? 0 : index / (count - 1);
      return Phaser.Math.Clamp(pathX + Math.sin(t * Math.PI * 2) * 26, 58, GAME_WIDTH - 58);
    }
    if (placement === "safe_followup") return Phaser.Math.Clamp(pathX + 34, 58, GAME_WIDTH - 58);
    return Phaser.Math.Clamp(pathX, 58, GAME_WIDTH - 58);
  }

  private toSongTime(counterTime: number) {
    return RUN_CAMPEON_SONG_SYNC_CONFIG.gameDurationSeconds - counterTime;
  }

  private toCounterTime(songElapsedTime: number) {
    return Math.max(0, RUN_CAMPEON_SONG_SYNC_CONFIG.gameDurationSeconds - songElapsedTime);
  }
}
